import type { ResultOf } from "@graphql-typed-document-node/core"
import { graphqlClient } from "@/lib/graphql"
import { graphql } from "@/__generated__"
import { RETRY_VOCABULARY_ENTRY, REVERT_VOCABULARY_CORRECTION } from "@/lib/vocabulary-entries"

// Longest selection accepted as a flashcard term (mirrors the server cap).
export const MAX_FLASHCARD_TEXT_LENGTH = 80

// `outcome` is the server's account of what happened (queued / already saved /
// skipped native language / unsupported language); `reused` says the returned
// entry pre-existed — never offer Undo on it, it may be the other participant's
// capture; `errorCode` is the stable code of a validation refusal (translated
// via captureErrorKey, `errors` keeps the sentence). `pending` stays for older
// clients. The entry is selected in full (the same shape as the subscription)
// so the classroom strip can list the capture the instant it is queued.
const CAPTURE_LESSON_VOCABULARY = graphql(`
  mutation CaptureLessonVocabulary($input: CaptureLessonVocabularyInput!) {
    captureLessonVocabulary(input: $input) {
      vocabularyEntry {
        id
        state
        outcome
        failureCode
        lessonId
        sourceMessageId
        createdByRole
        flashcardIds
        deckId
        sourceText
        correctedText
        correctionKind
        skipCorrection
        scheduledAt
        createdAt
        updatedAt
      }
      outcome
      reused
      pending
      errorCode
      errors
    }
  }
`)

// errorCode: not_authenticated | not_found | forbidden (the server's
// can_remove? — owner any time, tutor within the 60 s buffer).
const REMOVE_VOCABULARY_HIGHLIGHT = graphql(`
  mutation RemoveVocabularyHighlight($input: RemoveVocabularyHighlightInput!) {
    removeVocabularyHighlight(input: $input) {
      success
      errorCode
      errors
    }
  }
`)

export type CaptureResult = ResultOf<typeof CAPTURE_LESSON_VOCABULARY>["captureLessonVocabulary"]
export type RetryResult = ResultOf<typeof RETRY_VOCABULARY_ENTRY>["retryVocabularyEntry"]
export type RevertResult = ResultOf<typeof REVERT_VOCABULARY_CORRECTION>["revertVocabularyCorrection"]

// Capture highlighted lesson-notes text as a flashcard for the lesson's student.
// Works for either participant; the server derives student/tutor/language from
// the lesson and runs the existing AI generation pipeline (with a 60s buffer).
export async function captureLessonFlashcard(
  lessonId: string,
  text: string,
  contextText?: string,
): Promise<CaptureResult> {
  const res = await graphqlClient.request(
    CAPTURE_LESSON_VOCABULARY,
    { input: { lessonId, text, contextText } },
  )
  return res.captureLessonVocabulary
}

// Cancel a capture (the "undo"): the owner any time, the tutor within the 60s
// buffer. The server destroys any materialized cards.
export async function removeFlashcardCapture(
  vocabularyEntryId: string,
): Promise<{ success: boolean; errorCode: string | null; errors: string[] }> {
  const res = await graphqlClient.request(REMOVE_VOCABULARY_HIGHLIGHT, { input: { vocabularyEntryId } })
  return res.removeVocabularyHighlight
}

// Re-enqueue a failed capture (no 60s wait; the cached text is reused so the
// AI is never paid twice). errorCode: not_found | not_failed | not_authenticated.
export async function retryFlashcardCapture(id: string): Promise<RetryResult> {
  const res = await graphqlClient.request(RETRY_VOCABULARY_ENTRY, { input: { id } })
  return res.retryVocabularyEntry
}

// "Keep my spelling": drop the AI's correction and rebuild the card on what was
// actually captured. The work happens in the background — the caller shows a
// restoring state and lets the subscription deliver the finished entry.
// errorCode: not_found | not_correctable | in_progress | rate_limited | not_authenticated.
export async function revertFlashcardCorrection(id: string): Promise<RevertResult> {
  const res = await graphqlClient.request(REVERT_VOCABULARY_CORRECTION, { input: { id } })
  return res.revertVocabularyCorrection
}
